import type { ProductImageEntry, ProductImageText } from "./types";

/**
 * Resource surfaces (units, clients, employees, visit templates, visits and
 * scenarios) captured in the S5 scenario. Every image opens an existing view,
 * panel or dialog; no dialog is confirmed and nothing is saved.
 */
type Draft = {
  id: string;
  module: string;
  opens: string;
  setup: readonly string[];
  sv: ProductImageText;
  en: ProductImageText;
};

const NOT_SAVED_SV = "Dialogen stängs utan att något sparas.";
const NOT_SAVED_EN = "The dialog is closed without saving anything.";

function entry(draft: Draft): ProductImageEntry {
  return {
    id: draft.id,
    module: draft.module,
    area: "standing-data",
    opens: draft.opens,
    setup: draft.setup,
    scenario: "S5",
    visual: { variant: 0 },
    text: { sv: draft.sv, en: draft.en },
    use: ["help"],
  };
}

const unit = "/resources/service-areas/:id";
const client = "/resources/clients/:clientId";
const template = "/resources/clients/:clientId/care-plan/:templateId/edit";

export const CAMPAIGN_RESOURCES_CATALOGUE: readonly ProductImageEntry[] = [
  entry({
    id: "standing-data.unit-clone",
    module: "Klona enhet",
    opens: "/resources?resource=service-areas",
    setup: ["Öppna Klona enhet från enhetskortet", "Stäng utan att klona"],
    sv: {
      alt: "Panelen Klona enhet med källenhet och val av vad som kopieras",
      shows:
        "Panelen där en befintlig enhet väljs som källa och planeraren bestämmer om medarbetare, klienter, inställningar och beroenderegler följer med.",
      proves: `En ny enhet kan byggas från en befintlig enhets grunddata i stället för från början. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Clone unit panel with source unit and what to copy",
      shows:
        "The panel where an existing unit is chosen as the source and the planner decides whether employees, clients, settings and dependency rules come along.",
      proves: `A new unit can start from an existing unit's standing data instead of from scratch. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.data-quality-optimization-readiness",
    module: "Optimeringsberedskap",
    opens: "/resources/datahub?resource=all",
    setup: [
      "Öppna Datakvalitet för den klonade enheten",
      "Välj alla dataområden",
    ],
    sv: {
      alt: "Optimeringsberedskap med samlad status och åtgärder per dataområde",
      shows:
        "Optimeringsberedskap visar hur stor del av fälten som är ifyllda och vilka åtgärder som höjer kvaliteten på kontinuitet och effektivitet.",
      proves:
        "Planeraren ser vilka uppgifter som saknas innan schemat optimeras, med en direktlänk till varje åtgärd.",
    },
    en: {
      alt: "Optimization readiness with overall status and actions per data area",
      shows:
        "Optimization readiness shows how much of the data is complete and which actions improve continuity and efficiency.",
      proves:
        "The planner sees which details are missing before the schedule is optimized, with a direct link to each action.",
    },
  }),
  entry({
    id: "standing-data.data-quality-resource-tours",
    module: "Produktguider",
    opens: "/resources/datahub?section=guides",
    setup: ["Öppna Datakvalitet", "Välj Guider"],
    sv: {
      alt: "Produktguider för varje resursområde",
      shows:
        "En samlad lista med korta guider för klienter, medarbetare, besök och övriga resursområden.",
      proves:
        "Guiderna finns i produkten och förklarar fälten där uppgifterna fylls i.",
    },
    en: {
      alt: "Product guides for each resource area",
      shows:
        "One list of short guides for clients, employees, visits and the other resource areas.",
      proves:
        "The guides are part of the product and explain the fields where the details are entered.",
    },
  }),
  entry({
    id: "standing-data.unit-overview",
    module: "Enhetens översikt",
    opens: `${unit}?tab=overview`,
    setup: ["Öppna enheten på fliken Översikt"],
    sv: {
      alt: "Enhetens översikt med medarbetare, klienter, distrikt och besökstimmar",
      shows:
        "Översikten sammanfattar enhetens medarbetare, klienter, distrikt, arbetspassmallar och besökstimmar per dag.",
      proves:
        "Enhetens grunddata syns i en sammanfattning innan planeraren går vidare till schemat.",
    },
    en: {
      alt: "Unit overview with employees, clients, districts and visit hours",
      shows:
        "The overview summarises the unit's employees, clients, districts, shift templates and visit hours per day.",
      proves:
        "The unit's standing data is summarised before the planner moves on to the schedule.",
    },
  }),
  entry({
    id: "standing-data.unit-inset-flex",
    module: "Efterfrågan och insatsflex",
    opens: `${unit}?tab=overview#insatsflex`,
    setup: [
      "Öppna enhetens översikt",
      "Gå till avsnittet om efterfrågan och flex",
    ],
    sv: {
      alt: "Efterfrågan över dygnet bredvid insatsflex per insats",
      shows:
        "Diagrammet visar efterfrågan över dygnet och panelen bredvid visar hur mycket varje insats får flyttas.",
      proves:
        "Planeraren ser hur insatsflex påverkar topparna i efterfrågan innan något sparas för enheten.",
    },
    en: {
      alt: "Demand across the day next to visit flex per visit type",
      shows:
        "The chart shows demand across the day and the panel beside it shows how far each visit type may move.",
      proves:
        "The planner sees how visit flex affects demand peaks before anything is saved for the unit.",
    },
  }),
  entry({
    id: "standing-data.unit-inset-flex-apply",
    module: "Spara insatsflex för enheten",
    opens: `${unit}?tab=overview#insatsflex`,
    setup: [
      "Välj en rekommendation för insatsflex",
      "Öppna Spara för enheten",
      "Stäng utan att spara",
    ],
    sv: {
      alt: "Bekräftelse som listar ändrad insatsflex per insats",
      shows:
        "Bekräftelsen listar varje insats som får ny flex innan ändringen sparas för enheten.",
      proves: `Ändringar i insatsflex granskas rad för rad före sparning. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Confirmation listing changed visit flex per visit type",
      shows:
        "The confirmation lists every visit type that gets new flex before the change is saved for the unit.",
      proves: `Visit flex changes are reviewed line by line before saving. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.unit-district-map",
    module: "Distriktskarta",
    opens: `${unit}?tab=districts`,
    setup: ["Öppna enhetens distrikt"],
    sv: {
      alt: "Karta med enhetens distrikt och klienternas placering",
      shows:
        "Kartan visar varje distrikts täckningsyta i sin färg och klienterna som hör till distriktet.",
      proves:
        "Distriktsindelningen kan kontrolleras mot klienternas adresser direkt på kartan.",
    },
    en: {
      alt: "Map of the unit's districts and client locations",
      shows:
        "The map shows each district's coverage area in its colour and the clients that belong to it.",
      proves:
        "The district layout can be checked against client addresses directly on the map.",
    },
  }),
  entry({
    id: "standing-data.unit-district-supply-demand",
    module: "Tillgång och efterfrågan per distrikt",
    opens: `${unit}?tab=districts`,
    setup: ["Öppna enhetens distrikt", "Välj Förhandsgranska förslag"],
    sv: {
      alt: "Tabell med efterfrågan, nuvarande och föreslagen bemanning per distrikt",
      shows:
        "Tabellen jämför rekommenderad bemanning med nuvarande och föreslagna medarbetare och visar kvarvarande gap per distrikt.",
      proves:
        "Förslaget är en förhandsgranskning: planeraren ser om gapet stängs innan något sparas.",
    },
    en: {
      alt: "Table of demand, current and proposed staffing per district",
      shows:
        "The table compares recommended staffing with current and proposed employees and shows the remaining gap per district.",
      proves:
        "The proposal is a preview: the planner sees whether the gap closes before anything is saved.",
    },
  }),
  entry({
    id: "standing-data.unit-district-drawer",
    module: "Distriktets detaljer",
    opens: `${unit}?tab=districts`,
    setup: ["Öppna enhetens distrikt", "Välj Visa på ett distrikt"],
    sv: {
      alt: "Distriktspanel med distriktets klienter",
      shows:
        "Panelen visar distriktets klienter med adress och kommun, med flikar för medarbetare, gång och cykel.",
      proves:
        "Ett distrikts klienter och medarbetare granskas utan att lämna listan.",
    },
    en: {
      alt: "District panel with the district's clients",
      shows:
        "The panel lists the district's clients with address and municipality, with tabs for employees, walking and bicycle.",
      proves:
        "A district's clients and employees are reviewed without leaving the list.",
    },
  }),
  entry({
    id: "standing-data.unit-district-walking",
    module: "Gångdistrikt",
    opens: `${unit}?tab=districts`,
    setup: ["Öppna ett distrikt", "Välj fliken Gång"],
    sv: {
      alt: "Fliken Gång med startpunkt, radie och längsta restid",
      shows:
        "Fliken Gång anger startpunkt, radie och längsta restid för medarbetare som går i distriktet.",
      proves:
        "Gående medarbetare kan utgå från en egen startpunkt i distriktet. Inga ändringar har sparats.",
    },
    en: {
      alt: "The Walking tab with start point, radius and maximum travel time",
      shows:
        "The Walking tab sets the start point, radius and maximum travel time for employees who walk in the district.",
      proves:
        "Walking employees can start from their own point in the district. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.unit-district-bicycle",
    module: "Cykeldistrikt",
    opens: `${unit}?tab=districts`,
    setup: ["Öppna ett distrikt", "Välj fliken Cykel"],
    sv: {
      alt: "Fliken Cykel med startadress för distriktet",
      shows:
        "Fliken Cykel anger var medarbetare som cyklar i distriktet utgår ifrån.",
      proves:
        "Cyklande medarbetare kan få en egen startadress i distriktet. Inga ändringar har sparats.",
    },
    en: {
      alt: "The Bicycle tab with the district's start address",
      shows:
        "The Bicycle tab sets where employees who cycle in the district start from.",
      proves:
        "Cycling employees can get their own start address in the district. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.unit-district-delete",
    module: "Ta bort distrikt",
    opens: `${unit}?tab=districts`,
    setup: ["Välj Ta bort på ett distrikt", "Avbryt"],
    sv: {
      alt: "Bekräftelse innan ett distrikt tas bort",
      shows: "Bekräftelsen som visas innan ett distrikt tas bort från enheten.",
      proves: `Ett distrikt tas aldrig bort utan en uttrycklig bekräftelse. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Confirmation before a district is deleted",
      shows:
        "The confirmation shown before a district is removed from the unit.",
      proves: `A district is never deleted without explicit confirmation. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.employee-completion-workbench",
    module: "Datakomplettering för medarbetare",
    opens: `${unit}?tab=employees&workbench=data-completion`,
    setup: ["Öppna enhetens medarbetare", "Välj Datakomplettering"],
    sv: {
      alt: "Datakomplettering för medarbetare med antal klara rader och saknade uppgifter",
      shows:
        "Arbetsytan räknar klara rader och saknade datapunkter och filtrerar listan på det som återstår.",
      proves: "Saknade medarbetaruppgifter hittas och kompletteras i en lista.",
    },
    en: {
      alt: "Employee data completion with ready rows and missing details",
      shows:
        "The workspace counts ready rows and missing data points and filters the list to what remains.",
      proves: "Missing employee details are found and completed in one list.",
    },
  }),
  entry({
    id: "standing-data.client-completion-workbench",
    module: "Datakomplettering för klienter",
    opens: `${unit}?tab=clients&workbench=data-completion`,
    setup: ["Öppna enhetens klienter", "Välj Datakomplettering"],
    sv: {
      alt: "Datakomplettering för klienter med antal klara rader och saknade uppgifter",
      shows:
        "Arbetsytan räknar klara rader och saknade datapunkter och filtrerar klientlistan på det som återstår.",
      proves: "Saknade klientuppgifter hittas och kompletteras i en lista.",
    },
    en: {
      alt: "Client data completion with ready rows and missing details",
      shows:
        "The workspace counts ready rows and missing data points and filters the client list to what remains.",
      proves: "Missing client details are found and completed in one list.",
    },
  }),
  entry({
    id: "standing-data.batch-completion-preview",
    module: "Förhandsgranska batchkomplettering",
    opens: `${unit}?tab=clients&workbench=data-completion`,
    setup: [
      "Markera klienter i datakompletteringen",
      "Välj ett distrikt och Förhandsgranska",
      "Avbryt",
    ],
    sv: {
      alt: "Förhandsgranskning med före och efter för varje markerad klient",
      shows:
        "Förhandsgranskningen visar före och efter för varje rad och hur många rader som kan uppdateras.",
      proves: `En ändring på många klienter granskas rad för rad innan den sparas. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Preview with before and after for each selected client",
      shows:
        "The preview shows before and after for each row and how many rows can be updated.",
      proves: `A change across many clients is reviewed row by row before saving. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.skill-bulk-add",
    module: "Lägg till kompetens på flera",
    opens: `${unit}?tab=employees`,
    setup: ["Markera medarbetare", "Öppna Kompetens i verktygsraden"],
    sv: {
      alt: "Panelen Lägg till kompetens för markerade medarbetare",
      shows:
        "Panelen där en eller flera kompetenser läggs till på alla markerade medarbetare.",
      proves:
        "Kompetenser kan läggas till på flera medarbetare samtidigt. Inga ändringar har sparats.",
    },
    en: {
      alt: "The Add competencies panel for selected employees",
      shows:
        "The panel where one or more competencies are added to every selected employee.",
      proves:
        "Competencies can be added to several employees at once. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.shift-template-dialog",
    module: "Arbetspassmall",
    opens: `${unit}?tab=shift-templates`,
    setup: ["Öppna enhetens arbetspassmallar", "Välj Visa på en mall"],
    sv: {
      alt: "Panelen Arbetspassmall med tider, raster och veckodagar",
      shows:
        "Panelen visar arbetspassmallens tider, raster och de veckodagar mallen gäller.",
      proves: "Varje pass bygger på en hel arbetspassmall med egna raster.",
    },
    en: {
      alt: "The Shift template panel with times, breaks and weekdays",
      shows:
        "The panel shows the template's times, breaks and the weekdays it applies to.",
      proves:
        "Every shift is built from a whole shift template with its own breaks.",
    },
  }),
  entry({
    id: "standing-data.standing-shift-dialog",
    module: "Stående veckoskift",
    opens: `${unit}?tab=shifts`,
    setup: [
      "Öppna enhetens stående skift",
      "Öppna ett skift",
      "Stäng utan att spara",
    ],
    sv: {
      alt: "Panelen Redigera stående veckoskift för en medarbetare",
      shows:
        "Panelen visar ett återkommande veckoskift med medarbetare, veckodag, tider och arbetspassmall.",
      proves: `Stående skift beskriver medarbetarens tillgänglighet och ändrar inte planerade eller publicerade pass. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Edit standing weekly shift panel for an employee",
      shows:
        "The panel shows a recurring weekly shift with employee, weekday, times and shift template.",
      proves: `Standing shifts describe an employee's availability and do not change planned or published shifts. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.client-app-action",
    module: "Bjud in klient till appen",
    opens: `${unit}?tab=clients`,
    setup: ["Välj Bjud in i kolumnen App-status", "Avbryt"],
    sv: {
      alt: "Bekräftelse innan en klient bjuds in till appen",
      shows:
        "Bekräftelsen förklarar att en riktig inbjudan skickas till klientens e-postadress.",
      proves: `Ingen inbjudan skickas utan bekräftelse. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Confirmation before a client is invited to the app",
      shows:
        "The confirmation explains that a real invitation is sent to the client's email address.",
      proves: `No invitation is sent without confirmation. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.visit-template-create",
    module: "Nytt besök i genomförandeplanen",
    opens: `${unit}?tab=care-plan`,
    setup: ["Öppna enhetens besök", "Välj Nytt besök", "Stäng utan att skapa"],
    sv: {
      alt: "Panelen Välj klient för ett nytt besök",
      shows:
        "Ett nytt besök skapas alltid för en klient, så panelen börjar med att klienten väljs.",
      proves: `Besök i genomförandeplanen hör alltid till en klient. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Select client panel for a new care plan row",
      shows:
        "A new visit always belongs to a client, so the panel starts by choosing the client.",
      proves: `Care plan visits always belong to a client. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.visit-template-bulk-flex-margin",
    module: "Standardflex på flera besök",
    opens: `${unit}?tab=care-plan`,
    setup: ["Markera besök i genomförandeplanen", "Öppna Standardflex"],
    sv: {
      alt: "Panelen Sätt standardflex på markerade mallar",
      shows:
        "Panelen anger hur många minuter före och efter de markerade besöken får flyttas.",
      proves:
        "Flex kan sättas på många besök samtidigt och förhandsgranskas först. Inga ändringar har sparats.",
    },
    en: {
      alt: "The Set standard flex on selected templates panel",
      shows:
        "The panel sets how many minutes before and after the selected visits may move.",
      proves:
        "Flex can be set on many visits at once and is previewed first. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-template-bulk-inset",
    module: "Insats på flera besök",
    opens: `${unit}?tab=care-plan`,
    setup: ["Markera besök i genomförandeplanen", "Öppna Insats"],
    sv: {
      alt: "Panelen för att sätta insats på markerade besök",
      shows: "Panelen där en insats väljs för alla markerade besök.",
      proves:
        "Insats kan sättas på många besök samtidigt och förhandsgranskas först. Inga ändringar har sparats.",
    },
    en: {
      alt: "The panel for assigning a visit type to selected visits",
      shows:
        "The panel where one visit type is chosen for every selected visit.",
      proves:
        "A visit type can be set on many visits at once and is previewed first. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-template-bulk-time-window",
    module: "Tidsfönster på flera besök",
    opens: `${unit}?tab=care-plan`,
    setup: ["Markera besök i genomförandeplanen", "Öppna Tidsfönster"],
    sv: {
      alt: "Panelen Batchkomplettera tidsfönster med recept och minuter",
      shows:
        "Panelen väljer ett recept och antal minuter för nya tidsfönster på de markerade besöken.",
      proves:
        "Tidsfönster kompletteras för många besök på en gång och granskas innan de sparas. Inga ändringar har sparats.",
    },
    en: {
      alt: "The time window batch panel with recipe and minutes",
      shows:
        "The panel chooses a recipe and number of minutes for new time windows on the selected visits.",
      proves:
        "Time windows are completed for many visits at once and reviewed before saving. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-template-bulk-preview",
    module: "Förhandsgranska ändring på flera besök",
    opens: `${unit}?tab=care-plan`,
    setup: [
      "Markera besök i genomförandeplanen",
      "Öppna Standardflex och välj Förhandsgranska",
      "Avbryt",
    ],
    sv: {
      alt: "Förhandsgranskning med före och efter för varje markerat besök",
      shows:
        "Förhandsgranskningen visar före och efter för varje besök och vilka rader som inte ändras.",
      proves: `En ändring på många besök granskas rad för rad innan den sparas. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Preview with before and after for each selected visit",
      shows:
        "The preview shows before and after for each visit and which rows stay unchanged.",
      proves: `A change across many visits is reviewed row by row before saving. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.unit-schedules",
    module: "Enhetens scheman",
    opens: `${unit}?tab=schedules`,
    setup: ["Öppna enhetens flik Scheman"],
    sv: {
      alt: "Enhetens scheman med lösningar per schema",
      shows:
        "Fliken listar enhetens scheman och lösningarna för det valda schemat.",
      proves:
        "Alla scheman och lösningar för enheten nås från enhetens egen sida.",
    },
    en: {
      alt: "The unit's schedules with solutions per schedule",
      shows:
        "The tab lists the unit's schedules and the solutions for the selected schedule.",
      proves:
        "Every schedule and solution for the unit is reached from the unit's own page.",
    },
  }),
  entry({
    id: "standing-data.unit-form",
    module: "Ny enhet",
    opens: "/resources/service-areas/create",
    setup: ["Öppna formuläret för ny enhet", "Lämna utan att spara"],
    sv: {
      alt: "Formuläret för en ny enhet med namn och adress",
      shows:
        "Formuläret där en ny enhet får namn, adress och planeringsuppgifter.",
      proves:
        "En enhet skapas med adress, som blir start och slut för medarbetarnas rutter. Inga ändringar har sparats.",
    },
    en: {
      alt: "The new unit form with name and address",
      shows:
        "The form where a new unit gets its name, address and planning details.",
      proves:
        "A unit is created with an address that becomes the start and end of employee routes. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.client-page",
    module: "Klientens sida",
    opens: client,
    setup: ["Öppna en klient"],
    sv: {
      alt: "Klientens sida med enhet, distrikt, fast omsorgskontakt och flikar",
      shows:
        "Klientens sida samlar enhet, distrikt och fast omsorgskontakt överst, med flikar för grunddata, besök, schema och hälsa.",
      proves: "Allt om en klient nås från en sida.",
    },
    en: {
      alt: "Client page with unit, district, designated caregiver and tabs",
      shows:
        "The client page gathers unit, district and designated caregiver at the top, with tabs for basics, visits, schedule and health.",
      proves: "Everything about a client is reached from one page.",
    },
  }),
  entry({
    id: "standing-data.client-demand-cascade",
    module: "Gällande standardvärden för klienten",
    opens: `${client}?tab=advanced`,
    setup: ["Öppna klientens flik Avancerat"],
    sv: {
      alt: "Gällande standardvärden för klientens besök och var de kommer ifrån",
      shows:
        "Panelen visar vilka standardvärden som gäller för klientens besök och om de kommer från klienten, enheten eller organisationen.",
      proves: "Planeraren ser varifrån ett värde ärvs innan det ändras.",
    },
    en: {
      alt: "Effective demand defaults for the client and where they come from",
      shows:
        "The panel shows which defaults apply to the client's visits and whether they come from the client, unit or organization.",
      proves:
        "The planner sees where a value is inherited from before changing it.",
    },
  }),
  entry({
    id: "standing-data.client-care-plan-template-drawer",
    module: "Besök i klientens tidslinje",
    opens: `${client}?tab=visits`,
    setup: ["Öppna klientens besök", "Välj ett besök i tidslinjen"],
    sv: {
      alt: "Panel med besökets frekvens, tidsfönster och senast planerade tillfälle",
      shows:
        "Panelen visar besökets frekvens, tidsfönster och när det senast planerades, med en länk till planeringsvyn.",
      proves:
        "Ett besök i genomförandeplanen kan följas till de planerade tillfällena.",
    },
    en: {
      alt: "Panel with the visit's frequency, time window and last scheduled occurrence",
      shows:
        "The panel shows the visit's frequency, time window and when it was last scheduled, with a link to the planner.",
      proves: "A care plan visit can be followed to its scheduled occurrences.",
    },
  }),
  entry({
    id: "standing-data.care-plan-link-panel",
    module: "Vårdplan och beroenden",
    opens: `${client}?tab=visits`,
    setup: [
      "Öppna ett besök i klientens tidslinje",
      "Gå till Vårdplan och beroenden",
    ],
    sv: {
      alt: "Besökets krav, beroenden och visit-grupp",
      shows:
        "Panelen visar besökets krav, beroenden till andra besök och vilken visit-grupp det ingår i.",
      proves: "Beroenden mellan besök syns där besöket granskas.",
    },
    en: {
      alt: "The visit's requirements, dependencies and visit group",
      shows:
        "The panel shows the visit's requirements, dependencies on other visits and which visit group it belongs to.",
      proves:
        "Dependencies between visits are visible where the visit is reviewed.",
    },
  }),
  entry({
    id: "standing-data.client-care-plan-group-create-multi",
    module: "Skapa visit-grupp av markerade besök",
    opens: `${client}?tab=visits`,
    setup: [
      "Markera flera besök med Ctrl/Cmd-klick",
      "Välj Skapa visit-grupp av markerade besök",
      "Avbryt",
    ],
    sv: {
      alt: "Bekräftelse som listar de markerade besöken i en ny visit-grupp",
      shows:
        "Bekräftelsen listar alla markerade besök som blir en visit-grupp.",
      proves: `Flera besök kan grupperas på en gång, efter bekräftelse. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Confirmation listing the selected visits in a new visit group",
      shows:
        "The confirmation lists every selected visit that becomes a visit group.",
      proves: `Several visits can be grouped at once, after confirmation. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.client-schedule",
    module: "Klientens schema",
    opens: `${client}?tab=schedule`,
    setup: ["Öppna klientens flik Schema"],
    sv: {
      alt: "Klientens scheman och lösningar",
      shows:
        "Fliken listar de scheman och lösningar där klientens besök ingår.",
      proves: "Klientens plats i varje schema nås från klientens sida.",
    },
    en: {
      alt: "The client's schedules and solutions",
      shows:
        "The tab lists the schedules and solutions that include the client's visits.",
      proves:
        "The client's place in every schedule is reached from the client page.",
    },
  }),
  entry({
    id: "standing-data.client-health",
    module: "Klientens hälsa",
    opens: `${client}?tab=health`,
    setup: ["Öppna klientens flik Hälsa för det valda schemat"],
    sv: {
      alt: "Klienthälsa med sammanfattning och detaljkort",
      shows:
        "Fliken sammanfattar hur väl klientens besök är planerade i schemat, med detaljer om avvikelser.",
      proves: "Planeringskvaliteten för en enskild klient går att följa upp.",
    },
    en: {
      alt: "Client health with summary and detail cards",
      shows:
        "The tab summarises how well the client's visits are planned in the schedule, with details on deviations.",
      proves: "Planning quality for an individual client can be followed up.",
    },
  }),
  entry({
    id: "standing-data.client-monthly-care-recovery",
    module: "Bedömt vårdbehov att återhämta",
    opens: `${client}/kommunbeslut`,
    setup: ["Öppna klientens kommunbeslut"],
    sv: {
      alt: "Bedömt vårdbehov att återhämta per månad",
      shows:
        "Panelen visar per månad hur mycket beviljad tid som har bedömts behöva tas igen.",
      proves:
        "Bedömningen ändrar varken beviljad eller utförd tid och skapar inga besök.",
    },
    en: {
      alt: "Assessed care to recover per month",
      shows:
        "The panel shows per month how much granted time has been assessed as needing to be made up.",
      proves:
        "The assessment changes neither granted nor delivered time and creates no visits.",
    },
  }),
  entry({
    id: "standing-data.client-kommunbeslut",
    module: "Klientens kommunbeslut",
    opens: `${client}/kommunbeslut`,
    setup: ["Öppna klientens kommunbeslut"],
    sv: {
      alt: "Klientens kommunbeslut och månadsallokering",
      shows: "Sidan samlar klientens kommunbeslut och månadsallokering.",
      proves: "Biståndsbesluten finns på klienten, bredvid besöken de gäller.",
    },
    en: {
      alt: "The client's municipal decisions and monthly allocation",
      shows:
        "The page gathers the client's municipal decisions and monthly allocation.",
      proves:
        "Care decisions live on the client, next to the visits they cover.",
    },
  }),
  entry({
    id: "standing-data.client-archive",
    module: "Arkivera klient",
    opens: `${client}/edit`,
    setup: ["Öppna Avancerat och Aktiv period", "Välj Arkivera", "Avbryt"],
    sv: {
      alt: "Bekräftelse innan en klient arkiveras",
      shows:
        "Bekräftelsen förklarar att klienten försvinner ur schemaläggningen men att historiken behålls.",
      proves: `Arkivering behåller all historik och kräver bekräftelse. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Confirmation before a client is archived",
      shows:
        "The confirmation explains that the client leaves scheduling while the history is kept.",
      proves: `Archiving keeps all history and requires confirmation. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.client-form",
    module: "Ny klient",
    opens: "/resources/clients/create",
    setup: ["Öppna formuläret för ny klient", "Lämna utan att spara"],
    sv: {
      alt: "Formuläret för en ny klient med grunduppgifter och besöksadress",
      shows: "Formuläret där en ny klient får namn, enhet och besöksadress.",
      proves:
        "En klient läggs upp med besöksadress innan besöken schemaläggs. Inga ändringar har sparats.",
    },
    en: {
      alt: "The new client form with basic details and visit address",
      shows: "The form where a new client gets a name, unit and visit address.",
      proves:
        "A client is set up with a visit address before visits are planned. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.client-form-metadata",
    module: "Klientens metadata",
    opens: `${client}/edit`,
    setup: ["Öppna klientens formulär", "Gå till Avancerat och Metadata"],
    sv: {
      alt: "Klientens metadata med tidsstämplar och externa id",
      shows:
        "Avsnittet visar när klienten skapades och ändrades, externa id och uppgifter från importen.",
      proves:
        "Klientens ursprung och ändringar går att spåra. Inga ändringar har sparats.",
    },
    en: {
      alt: "Client metadata with timestamps and external IDs",
      shows:
        "The section shows when the client was created and changed, external IDs and import details.",
      proves:
        "The client's origin and changes can be traced. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.client-form-visits",
    module: "Klientens besök i formuläret",
    opens: `${client}/edit`,
    setup: ["Öppna klientens formulär", "Välj fliken Besök"],
    sv: {
      alt: "Fliken Besök i klientformuläret",
      shows:
        "Fliken listar klientens besök i genomförandeplanen och låter planeraren lägga till fler.",
      proves:
        "Klientens uppgifter och besök redigeras i samma formulär. Inga ändringar har sparats.",
    },
    en: {
      alt: "The Visits tab in the client form",
      shows:
        "The tab lists the client's care plan visits and lets the planner add more.",
      proves:
        "A client's details and visits are edited in the same form. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.employee-page",
    module: "Medarbetarens sida",
    opens: "/resources/employees/:id",
    setup: ["Öppna en medarbetare från listan"],
    sv: {
      alt: "Medarbetarens sida med flikar för grunddata, avancerat och schema",
      shows:
        "Medarbetarens sida samlar identitet, enhet och flikar för grunddata, avancerat och schema.",
      proves: "Allt om en medarbetare nås från en sida.",
    },
    en: {
      alt: "Employee page with tabs for basics, advanced and schedule",
      shows:
        "The employee page gathers identity, unit and tabs for basics, advanced and schedule.",
      proves: "Everything about an employee is reached from one page.",
    },
  }),
  entry({
    id: "standing-data.employee-placeholder",
    module: "Platshållare",
    opens: "/resources/employees/:id",
    setup: ["Öppna en platshållare som används i ett schema"],
    sv: {
      alt: "Medarbetarsida för en platshållare med förklarande banner",
      shows:
        "Bannern förklarar att raden är en platshållare och att dess pass behöver en riktig medarbetare före publicering.",
      proves:
        "Platshållare syns tydligt och kan inte förväxlas med riktiga medarbetare.",
    },
    en: {
      alt: "Employee page for a placeholder with an explanatory banner",
      shows:
        "The banner explains that the row is a placeholder and that its shifts need a real employee before publishing.",
      proves:
        "Placeholders are clearly marked and cannot be mistaken for real employees.",
    },
  }),
  entry({
    id: "standing-data.employee-roster-shift",
    module: "Pass i medarbetarens veckoschema",
    opens: "/resources/employees/:id?tab=advanced",
    setup: [
      "Öppna medarbetarens flik Avancerat",
      "Välj ett pass i veckoschemat",
      "Stäng utan att spara",
    ],
    sv: {
      alt: "Panelen Redigera stående veckoskift från medarbetarens veckoschema",
      shows:
        "Panelen visar passets veckodag, tider och arbetspassmall i medarbetarens veckoschema.",
      proves: `Medarbetarens återkommande pass används som kapacitet och ändrar inte publicerade pass. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Edit standing weekly shift panel from the employee's weekly schedule",
      shows:
        "The panel shows the shift's weekday, times and shift template in the employee's weekly schedule.",
      proves: `The employee's recurring shifts are used as capacity and do not change published shifts. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.employee-schedule",
    module: "Medarbetarens schema",
    opens: "/resources/employees/:id?tab=schedule",
    setup: ["Öppna medarbetarens flik Schema"],
    sv: {
      alt: "Medarbetarens scheman och lösningar",
      shows:
        "Fliken listar de scheman och lösningar där medarbetaren har pass.",
      proves: "Medarbetarens pass i varje schema nås från medarbetarens sida.",
    },
    en: {
      alt: "The employee's schedules and solutions",
      shows:
        "The tab lists the schedules and solutions where the employee has shifts.",
      proves:
        "The employee's shifts in every schedule are reached from the employee page.",
    },
  }),
  entry({
    id: "standing-data.employee-form",
    module: "Ny medarbetare",
    opens: "/resources/employees/create",
    setup: ["Öppna formuläret för ny medarbetare", "Lämna utan att spara"],
    sv: {
      alt: "Formuläret för en ny medarbetare med identitet och enhet",
      shows: "Formuläret där en ny medarbetare får namn, enhet och roll.",
      proves:
        "Varje medarbetare hör till en enhet från början. Inga ändringar har sparats.",
    },
    en: {
      alt: "The new employee form with identity and unit",
      shows: "The form where a new employee gets a name, unit and role.",
      proves:
        "Every employee belongs to a unit from the start. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.employee-form-metadata",
    module: "Medarbetarens administration",
    opens: "/resources/employees/:id/edit",
    setup: [
      "Öppna medarbetarens formulär",
      "Gå till Avancerat och Administration",
    ],
    sv: {
      alt: "Medarbetarens administration med tidsstämplar och externa id",
      shows:
        "Avsnittet visar när medarbetaren skapades och ändrades, externa id och uppgifter från importen.",
      proves:
        "Medarbetarens ursprung och ändringar går att spåra. Inga ändringar har sparats.",
    },
    en: {
      alt: "Employee administration with timestamps and external IDs",
      shows:
        "The section shows when the employee was created and changed, external IDs and import details.",
      proves:
        "The employee's origin and changes can be traced. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-template-window-timeline",
    module: "Tidsfönster på dygnets tidslinje",
    opens: template,
    setup: ["Öppna besöksmallen", "Gå till Tidsfönster under Grunder"],
    sv: {
      alt: "Dygnsremsa med önskat och tillåtet tidsfönster för besöket",
      shows:
        "Remsan visar besökets önskade fönster, det tillåtna fönstret och besökets längd på dygnets tidslinje.",
      proves:
        "Tidsfönstret går att läsa av direkt innan mallen sparas. Inga ändringar har sparats.",
    },
    en: {
      alt: "Day strip with the visit's preferred and allowed time window",
      shows:
        "The strip shows the visit's preferred window, the allowed window and the visit length on the day's timeline.",
      proves:
        "The time window can be read at a glance before the template is saved. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-template-scheduling-preview",
    module: "Klientens tidslinje i besöksmallen",
    opens: template,
    setup: ["Öppna besöksmallen", "Läs Klientens tidslinje"],
    sv: {
      alt: "Förhandsvisning av besökets tidsfönster bland klientens övriga besök",
      shows:
        "Förhandsvisningen placerar besökets tidsfönster bland klientens befintliga besök och markerar överlapp.",
      proves: "Krockar med klientens andra besök syns innan mallen sparas.",
    },
    en: {
      alt: "Preview of the visit's time window among the client's other visits",
      shows:
        "The preview places the visit's time window among the client's existing visits and marks overlaps.",
      proves:
        "Clashes with the client's other visits are visible before the template is saved.",
    },
  }),
  entry({
    id: "standing-data.visit-template-advanced",
    module: "Beroenden i besöksmallen",
    opens: template,
    setup: ["Öppna besöksmallen", "Välj fliken Avancerat"],
    sv: {
      alt: "Fliken Avancerat med besökets beroenden",
      shows:
        "Fliken anger vilka besök som måste ske före eller efter detta och med vilket avstånd.",
      proves:
        "Ordningen mellan en klients besök är en del av genomförandeplanen. Inga ändringar har sparats.",
    },
    en: {
      alt: "The Advanced tab with the visit's dependencies",
      shows:
        "The tab sets which visits must happen before or after this one and with what gap.",
      proves:
        "The order of a client's visits is part of the care plan. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-template-caregiver-preference",
    module: "Medarbetarlås i besöksmallen",
    opens: template,
    setup: [
      "Öppna besöksmallen",
      "Välj fliken Valfritt och gå till medarbetare",
    ],
    sv: {
      alt: "Krav och önskemål om medarbetare för besöket",
      shows:
        "Avsnittet anger vilka medarbetare som krävs eller önskas för besöket.",
      proves: "Kontinuitet kan styras per besök. Inga ändringar har sparats.",
    },
    en: {
      alt: "Required and preferred employees for the visit",
      shows:
        "The section sets which employees are required or preferred for the visit.",
      proves:
        "Continuity can be steered per visit. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-template-metadata",
    module: "Besöksmallens metadata",
    opens: template,
    setup: ["Öppna besöksmallen", "Fäll ut Metadata"],
    sv: {
      alt: "Besöksmallens anteckningar och underlag från importen",
      shows:
        "Metadata visar mallens anteckningar, externa id och underlag från importen.",
      proves:
        "Det går att se varifrån en besöksmall kommer. Inga ändringar har sparats.",
    },
    en: {
      alt: "The visit template's notes and import evidence",
      shows:
        "Metadata shows the template's notes, external IDs and evidence from the import.",
      proves:
        "It is possible to see where a visit template came from. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-template-projected-visits",
    module: "Besök från mallen i scheman",
    opens: template,
    setup: ["Öppna besöksmallen", "Gå till besök som projicerats från mallen"],
    sv: {
      alt: "Lista över planerade besök som skapats från besöksmallen",
      shows:
        "Listan visar de senaste planerade besöken från mallen och om de är tilldelade.",
      proves:
        "En besöksmall kan följas till besöken den har gett upphov till i scheman.",
    },
    en: {
      alt: "List of planned visits created from the visit template",
      shows:
        "The list shows the latest planned visits from the template and whether they are assigned.",
      proves:
        "A visit template can be followed to the visits it produced in schedules.",
    },
  }),
  entry({
    id: "standing-data.visit-form",
    module: "Redigera besök",
    opens: "/resources/visits/:id/edit",
    setup: ["Öppna ett besök i schemat för redigering", "Lämna utan att spara"],
    sv: {
      alt: "Formuläret för ett besök med schema, klient och mall",
      shows:
        "Formuläret visar vilket schema, vilken klient och vilken mall besöket hör till.",
      proves:
        "Ett enskilt besök i ett schema kan granskas och justeras. Inga ändringar har sparats.",
    },
    en: {
      alt: "The visit form with schedule, client and template",
      shows:
        "The form shows which schedule, client and template the visit belongs to.",
      proves:
        "A single visit in a schedule can be reviewed and adjusted. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-form-planning",
    module: "Besökets planering",
    opens: "/resources/visits/:id/edit",
    setup: ["Öppna besöksformuläret", "Välj fliken Avancerat"],
    sv: {
      alt: "Planeringsuppgifter för besöket med tidsfönster och låsning",
      shows:
        "Avsnittet visar besökets tidsfönster, begränsningar och om besöket är låst till en medarbetare.",
      proves:
        "Planeringsreglerna för ett enskilt besök syns samlat. Inga ändringar har sparats.",
    },
    en: {
      alt: "Planning details for the visit with time windows and pinning",
      shows:
        "The section shows the visit's time windows, constraints and whether it is pinned to an employee.",
      proves:
        "The planning rules for a single visit are shown together. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-form-details",
    module: "Besökets detaljer",
    opens: "/resources/visits/:id/edit",
    setup: ["Öppna besöksformuläret", "Välj fliken Valfritt"],
    sv: {
      alt: "Besökets detaljer med insats, adress och anteckningar",
      shows: "Fliken visar besökets insats, besöksadress och anteckningar.",
      proves:
        "Tjänstedetaljer för besöket finns på ett ställe. Inga ändringar har sparats.",
    },
    en: {
      alt: "Visit details with visit type, address and notes",
      shows: "The tab shows the visit's type, visit address and notes.",
      proves:
        "Service details for the visit are in one place. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-form-skills",
    module: "Besökets kompetenser och beroenden",
    opens: "/resources/visits/:id/edit",
    setup: [
      "Öppna besöksformuläret",
      "Välj fliken Avancerat och gå till kompetenser",
    ],
    sv: {
      alt: "Kompetenser, taggar och beroenden för besöket",
      shows:
        "Avsnittet anger vilka kompetenser besöket kräver och vilka besök det beror på.",
      proves:
        "Besökets krav på kompetens och ordning följer med till optimeringen. Inga ändringar har sparats.",
    },
    en: {
      alt: "Competencies, tags and dependencies for the visit",
      shows:
        "The section sets which competencies the visit requires and which visits it depends on.",
      proves:
        "The visit's competency and order requirements are carried into optimization. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.visit-detail",
    module: "Besök",
    opens: "/resources/visits/:id",
    setup: ["Öppna ett besök i schemat"],
    sv: {
      alt: "Besökets sida med tid, klient, insats och relationer",
      shows:
        "Sidan visar besökets tid, klient, insats och kopplingar till mall och schema.",
      proves:
        "Ett planerat besök kan följas tillbaka till klienten och genomförandeplanen.",
    },
    en: {
      alt: "Visit page with time, client, visit type and relations",
      shows:
        "The page shows the visit's time, client, visit type and links to template and schedule.",
      proves:
        "A planned visit can be traced back to the client and the care plan.",
    },
  }),
  entry({
    id: "standing-data.visit-detail-client-message",
    module: "Meddelande till kund eller anhörig",
    opens: "/resources/visits/:id",
    setup: ["Öppna ett besök", "Gå till Meddelande till kund/anhörig"],
    sv: {
      alt: "Formuläret Meddelande till kund/anhörig med mottagare och text",
      shows:
        "Formuläret där planeraren väljer mottagare och skriver ett meddelande om besöket.",
      proves:
        "Kund och anhöriga kan informeras direkt från besöket. Inget meddelande har skickats.",
    },
    en: {
      alt: "The Message to client/family form with recipient and text",
      shows:
        "The form where the planner chooses a recipient and writes a message about the visit.",
      proves:
        "Clients and family can be informed directly from the visit. No message has been sent.",
    },
  }),
  entry({
    id: "standing-data.visit-group",
    module: "Visit-grupp",
    opens: "/resources/visit-groups/:id",
    setup: ["Öppna en visit-grupp i schemat"],
    sv: {
      alt: "Visit-gruppens sida med de besök som ingår",
      shows: "Sidan visar visit-gruppen och de besök som planeras tillsammans.",
      proves:
        "Besök som hör ihop, till exempel dubbelbemanning, hålls samman i planeringen.",
    },
    en: {
      alt: "Visit group page with its member visits",
      shows: "The page shows the visit group and the visits planned together.",
      proves:
        "Visits that belong together, such as double staffing, are kept together in planning.",
    },
  }),
  entry({
    id: "standing-data.catalog-demand",
    module: "Efterfrågan per månad",
    opens: "/catalog/demand",
    setup: ["Öppna efterfrågan för katalogen"],
    sv: {
      alt: "Efterfrågan per månad med nyckeltal och besöksmallar",
      shows:
        "Sidan visar månadens efterfrågan som nyckeltal och listar besöksmallarna bakom den.",
      proves: "Efterfrågan räknas från genomförandeplanens besöksmallar.",
    },
    en: {
      alt: "Monthly demand with key figures and visit templates",
      shows:
        "The page shows the month's demand as key figures and lists the visit templates behind it.",
      proves: "Demand is calculated from the care plan's visit templates.",
    },
  }),
  entry({
    id: "standing-data.scenario",
    module: "Scenario",
    opens: "/resources/scenarios/:id",
    setup: ["Öppna ett befintligt scenario"],
    sv: {
      alt: "Scenariots sida med sammanfattning och viktning",
      shows:
        "Sidan visar scenariots sammanfattning, ändringar i underlaget och hur målen viktas.",
      proves:
        "Ett scenario beskriver hur optimeringen prioriterar, och kan granskas innan det används. Inga ändringar har sparats.",
    },
    en: {
      alt: "Scenario page with summary and weighting",
      shows:
        "The page shows the scenario's summary, changes to the input data and how the goals are weighted.",
      proves:
        "A scenario describes how optimization prioritises and can be reviewed before use. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.scenario-create",
    module: "Nytt anpassat scenario",
    opens: "/resources/scenarios/new",
    setup: ["Öppna formuläret för nytt scenario", "Lämna utan att spara"],
    sv: {
      alt: "Plattformsadministrationens formulär för ett nytt scenario",
      shows:
        "Formuläret där Caires plattformsadministration ger ett nytt scenario namn, beskrivning och planeringsflöde.",
      proves:
        "Scenarier underhålls av Caire på plattformen; kunder skapar eller ändrar inga scenarier. Inga ändringar har sparats.",
    },
    en: {
      alt: "The platform administration's new scenario form",
      shows:
        "The form where Caire's platform administration gives a new scenario its name, description and planning pipeline.",
      proves:
        "Scenarios are maintained by Caire on the platform; customers do not create or edit scenarios. No changes have been saved.",
    },
  }),
  entry({
    id: "standing-data.scenario-management-sheet",
    module: "Nytt scenario för enheten",
    opens: `${unit}?tab=overview`,
    setup: [
      "Öppna Regler och scenarion i enhetens inställningar",
      "Välj Nytt scenario",
      "Stäng utan att spara",
    ],
    sv: {
      alt: "Plattformsadministrationens panel för ett nytt scenario på enheten",
      shows:
        "Panelen där Caires plattformsadministration ger ett scenario för enheten namn och prioriteringar.",
      proves: `Regler och scenarier på enheten är en plattformsyta; kunder skapar eller ändrar inga scenarier. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The platform administration's new scenario panel for the unit",
      shows:
        "The panel where Caire's platform administration gives a scenario for the unit its name and priorities.",
      proves: `Rules and scenarios on the unit are a platform surface; customers do not create or edit scenarios. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.unit-demo-sandbox",
    module: "Demoenhetens sandlåda",
    opens: `${unit}?tab=overview`,
    setup: ["Öppna demoenheten Nacka"],
    sv: {
      alt: "Banner som visar att enheten är en sandlåda med syntetisk grunddata",
      shows:
        "Bannern överst på demoenheten förklarar att enheten är en sandlåda med syntetisk grunddata och kan återställas.",
      proves:
        "Demoenheten är tydligt skild från organisationens riktiga enheter.",
    },
    en: {
      alt: "Banner showing that the unit is a sandbox with synthetic standing data",
      shows:
        "The banner at the top of the demo unit explains that the unit is a sandbox with synthetic standing data and can be reset.",
      proves:
        "The demo unit is clearly separated from the organization's real units.",
    },
  }),
  entry({
    id: "standing-data.unit-kommunbeslut",
    module: "Kommunbeslut för enheten",
    opens: `${unit}?tab=kommunbeslut`,
    setup: ["Öppna enhetens flik Kommunbeslut"],
    sv: {
      alt: "Fliken Kommunbeslut för enheten",
      shows: "Fliken Kommunbeslut har en egen plats bland enhetens flikar.",
      proves:
        "Biståndsbesluten registreras på varje klient, under klientens kommunbeslut.",
    },
    en: {
      alt: "The Municipal decisions tab for the unit",
      shows:
        "The Municipal decisions tab has its own place among the unit's tabs.",
      proves:
        "Care decisions are recorded on each client, under the client's municipal decisions.",
    },
  }),
  entry({
    id: "standing-data.unit-purge-blocked",
    module: "Enheten kan inte raderas ännu",
    opens: "/resources/service-areas/:id/edit",
    setup: ["Öppna en importerad enhet", "Gå till Riskzon"],
    sv: {
      alt: "Riskzon som förklarar varför enheten inte kan raderas",
      shows:
        "Riskzonen listar det som fortfarande hör till enheten och hindrar permanent radering.",
      proves:
        "En enhet kan inte raderas så länge scheman eller kopplingar hänger kvar.",
    },
    en: {
      alt: "Danger zone explaining why the unit cannot be deleted",
      shows:
        "The danger zone lists what still belongs to the unit and prevents permanent deletion.",
      proves:
        "A unit cannot be deleted while schedules or links still depend on it.",
    },
  }),
  entry({
    id: "standing-data.unit-clients-empty",
    module: "Inga klienter ännu",
    opens: `${unit}?tab=clients`,
    setup: ["Öppna klienterna för en enhet utan klienter"],
    sv: {
      alt: "Tom klientlista med val för att lägga till eller importera klienter",
      shows:
        "En enhet utan klienter visar hur klienter läggs till en och en eller importeras.",
      proves: "En ny enhet leder planeraren direkt till nästa steg.",
    },
    en: {
      alt: "Empty client list with options to add or import clients",
      shows:
        "A unit without clients shows how to add clients one by one or import them.",
      proves: "A new unit guides the planner straight to the next step.",
    },
  }),
  entry({
    id: "standing-data.unit-employees-empty",
    module: "Inga medarbetare ännu",
    opens: `${unit}?tab=employees`,
    setup: ["Öppna medarbetarna för en enhet utan medarbetare"],
    sv: {
      alt: "Tom medarbetarlista med val för att lägga till eller importera medarbetare",
      shows:
        "En enhet utan medarbetare visar hur medarbetare läggs till en och en eller importeras.",
      proves: "En ny enhet leder planeraren direkt till nästa steg.",
    },
    en: {
      alt: "Empty employee list with options to add or import employees",
      shows:
        "A unit without employees shows how to add employees one by one or import them.",
      proves: "A new unit guides the planner straight to the next step.",
    },
  }),
  entry({
    id: "standing-data.client-unarchive",
    module: "Återställ klient",
    opens: `${client}/edit`,
    setup: [
      "Öppna en arkiverad klient",
      "Välj Återställ klient under Aktiv period",
      "Avbryt",
    ],
    sv: {
      alt: "Bekräftelse innan en arkiverad klient återställs",
      shows:
        "Bekräftelsen förklarar att klienten läggs tillbaka i schemaläggningen.",
      proves: `En arkiverad klient kan återställas med all historik kvar. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Confirmation before an archived client is restored",
      shows: "The confirmation explains that the client returns to scheduling.",
      proves: `An archived client can be restored with all history intact. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.client-care-plan-group-create",
    module: "Skapa visit-grupp",
    opens: `${client}?tab=visits`,
    setup: ["Dra ett besök till ett annat i klientens tidslinje", "Avbryt"],
    sv: {
      alt: "Bekräftelse för att skapa en visit-grupp av två besök",
      shows: "Bekräftelsen namnger de två besöken som blir en visit-grupp.",
      proves: `Besök grupperas bara efter bekräftelse. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Confirmation to create a visit group from two visits",
      shows: "The confirmation names the two visits that become a visit group.",
      proves: `Visits are grouped only after confirmation. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "standing-data.proactive-clinical-review",
    module: "Proaktiv vårdgenomgång",
    opens: `${unit}?tab=care-plan`,
    setup: [
      "Öppna besöken för en enhet med aktiverad vårdgenomgång",
      "Välj klienter utan att starta",
    ],
    sv: {
      alt: "Formuläret för proaktiv vårdgenomgång med val av klienter",
      shows:
        "Formuläret där planeraren väljer upp till 25 klienter för en genomgång av genomförandeplanerna.",
      proves:
        "Genomgången startas av planeraren och ändrar inget utan granskning. Ingen genomgång har startats.",
    },
    en: {
      alt: "The proactive care review form with client selection",
      shows:
        "The form where the planner selects up to 25 clients for a review of their care plans.",
      proves:
        "The review is started by the planner and changes nothing without review. No review has been started.",
    },
  }),
  entry({
    id: "standing-data.visit-detail-decision-timeline",
    module: "Operativ beslutstidslinje",
    opens: "/resources/visits/:id",
    setup: ["Öppna ett besök med operativa beslut"],
    sv: {
      alt: "Besökets operativa beslutstidslinje med händelser i ordning",
      shows:
        "Tidslinjen listar besökets operativa beslut i ordning, med vem som fattade dem.",
      proves:
        "Historiken är skrivskyddad och verifierad, så varje beslut om besöket går att följa i efterhand.",
    },
    en: {
      alt: "The visit's operational decision timeline with events in order",
      shows:
        "The timeline lists the visit's operational decisions in order, with who made them.",
      proves:
        "The history is read-only and verified, so every decision about the visit can be traced afterwards.",
    },
  }),
  entry({
    id: "standing-data.visit-detail-import-data",
    module: "Import och mall för besöket",
    opens: "/resources/visits/:id",
    setup: ["Öppna ett importerat besök", "Fäll ut Import och mall"],
    sv: {
      alt: "Utfällt avsnitt med besökets importerade uppgifter och mallens källdata",
      shows:
        "Avsnittet visar uppgifterna som följde med besöket från importen och mallens källrad.",
      proves:
        "Ett importerat besök kan följas tillbaka till raden det kom från.",
    },
    en: {
      alt: "Expanded section with the visit's imported details and the template's source data",
      shows:
        "The section shows the details that came with the visit from the import and the template's source row.",
      proves: "An imported visit can be traced back to the row it came from.",
    },
  }),
];
